/**
 * Stockpile geometry primitives — pure XY 2D math, no Three.js, no Potree.
 *
 * `polygon` is a closed-or-open ring of [x, y] pairs (Z is ignored). The
 * implementation handles both shapes — the closing duplicate vertex is optional.
 *
 * Reference: classic ray-casting / horizontal-half-line algorithm
 * (Sedgewick / Foley & van Dam). On-edge points are reported as inside; this is
 * fine for our cell-centre sampling because edge cases land in << 1 cell out of
 * tens of thousands and the integer parity of the ray crossings stays
 * deterministic for repeat compute.
 */
export function pointInPolygon2D(x: number, y: number, polygon: Array<[number, number]>): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  // Iterate over polygon edges (i, j) where j is the previous vertex.
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    // Standard ray-cast: count crossings of the +X-direction ray from (x, y).
    // The (yi > y) !== (yj > y) check excludes horizontal edges and ensures we
    // only count crossings on edges that straddle the horizontal scan line.
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-30) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Axis-aligned bounding box of a polygon in XY (Z ignored). */
export function polygonAabb(polygon: Array<[number, number]>): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of polygon) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Best-fit plane through a set of 3-D points via least-squares.
 *
 * **Coordinates are centred** at the points' XYZ centroid before the fit, then
 * the plane is returned in the centred frame as
 *
 *   z − z0 = a·(x − x0) + b·(y − y0) + c0
 *
 * This centring is *essential* for numerical stability. Stockpile polygons live
 * in projected coordinates (UTM-style SRIDs with values in the millions), so a
 * naïve fit in raw coordinates produces a normal-equation matrix whose entries
 * are O(10¹²); the 3×3 determinant is then the near-cancelling difference of
 * three O(10²⁴) terms, which under float64 retains essentially zero precision.
 * That manifests as wildly wrong slope coefficients (a, b ≈ 10⁻³ noise instead
 * of zero), which when evaluated at `cx ≈ 600 000` gives base-elevation errors
 * of hundreds of metres — or worse. Centering reduces the matrix entries to
 * O(footprint²) and keeps the fit accurate to a few ULP.
 *
 * Use {@link evalBestFitPlane} to evaluate the returned plane at any (x, y).
 *
 * Returns `null` when the system is singular — happens for collinear input
 * (e.g. two-point polygons after de-duplication, which the UI rejects upstream).
 */
export interface BestFitPlane {
  /** Slope along x (in centred frame). */
  a: number;
  /** Slope along y (in centred frame). */
  b: number;
  /** Intercept in centred frame: `z − z0 = a·(x−x0) + b·(y−y0) + c0`. */
  c0: number;
  /** Centring offset for x (the x-centroid of the input points). */
  x0: number;
  /** Centring offset for y (the y-centroid of the input points). */
  y0: number;
  /** Centring offset for z (the z-centroid of the input points). */
  z0: number;
}

export function fitPlaneZxy(points: Array<[number, number, number]>): BestFitPlane | null {
  const n = points.length;
  if (n < 3) return null;

  // First pass: compute centroids.
  let cx0 = 0;
  let cy0 = 0;
  let cz0 = 0;
  for (const [x, y, z] of points) {
    cx0 += x;
    cy0 += y;
    cz0 += z;
  }
  cx0 /= n;
  cy0 /= n;
  cz0 /= n;

  // Second pass: accumulate centred sums. With centring, sx = sy = sz = 0 by
  // construction, so the normal-equation matrix collapses to
  //   ┌ sxx  sxy ┐ ┌a┐   ┌sxz┐
  //   └ sxy  syy ┘ └b┘ = └syz┘
  // (the c0 row decouples and gives c0 = 0 in the centred frame, but we still
  // return c0 explicitly so the eval function has one place to add cz0).
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  let sxz = 0;
  let syz = 0;
  for (const [x, y, z] of points) {
    const dx = x - cx0;
    const dy = y - cy0;
    const dz = z - cz0;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
    sxz += dx * dz;
    syz += dy * dz;
  }

  // 2×2 determinant in the centred frame.
  const det = sxx * syy - sxy * sxy;
  // Threshold scaled to sxx + syy so it adapts to the polygon's footprint —
  // a 1 cm polygon and a 1 km polygon both need their *own* scale of noise
  // floor. 1e-12 of the trace is comfortably below any meaningful signal.
  if (Math.abs(det) < (sxx + syy) * 1e-12 + 1e-18) return null;

  const a = (sxz * syy - syz * sxy) / det;
  const b = (sxx * syz - sxy * sxz) / det;

  return { a, b, c0: 0, x0: cx0, y0: cy0, z0: cz0 };
}

/**
 * Evaluate a best-fit plane at world (x, y). Always uses the centred-frame
 * formulation to preserve precision — never collapse this back into a flat
 * `a·x + b·y + c` form, that defeats the whole point of fitting in centred
 * coords.
 */
export function evalBestFitPlane(plane: BestFitPlane, x: number, y: number): number {
  return plane.a * (x - plane.x0) + plane.b * (y - plane.y0) + plane.c0 + plane.z0;
}

/** XY centroid of a polygon (simple average — fine for label placement). */
export function polygonCentroid2D(polygon: Array<[number, number]>): [number, number] {
  let sx = 0;
  let sy = 0;
  for (const [x, y] of polygon) {
    sx += x;
    sy += y;
  }
  return [sx / polygon.length, sy / polygon.length];
}

/**
 * Re-order polygon vertices so they walk around the polygon's XY centroid in a
 * consistent angular sweep (CCW). Forgives any click order that produces a
 * star-shaped polygon — i.e. any polygon whose centroid is inside the polygon
 * — which covers virtually every real-world stockpile footprint and lots of
 * "I picked the corners in random order" failure modes.
 *
 * **Limitation**: For genuinely concave outlines (U-shapes, donuts, anything
 * not star-shaped from its centroid) this returns a polygon that visits the
 * same vertices but cuts across the concavity. Stockpiles don't have those,
 * but it's worth knowing if we ever extend this to other tools.
 *
 * Z values are preserved per-vertex (this only re-orders, never re-projects).
 */
export function sortVerticesByCentroidAngle(
  verts: Array<[number, number, number]>,
): Array<[number, number, number]> {
  if (verts.length < 3) return [...verts];
  let cx = 0;
  let cy = 0;
  for (const v of verts) {
    cx += v[0];
    cy += v[1];
  }
  cx /= verts.length;
  cy /= verts.length;
  return [...verts].sort(
    (a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx),
  );
}

/**
 * Extract the outer boundary ring of a collection of polygons by edge
 * topology. Designed for TINs but works for any input.
 *
 * **Algorithm (single-pass, O(n))**:
 *   1. Walk every edge of every input polygon (outer rings only — holes
 *      would invert the count and break boundary detection).
 *   2. Each edge `(a, b)` is canonicalised by sorting its endpoint keys, and
 *      a counter is incremented in a Map.
 *   3. Boundary edges are those with count `=== 1` — interior edges in a
 *      TIN are shared between two adjacent triangles, so they hit count 2.
 *   4. Build an adjacency map over boundary edges and walk rings: pick an
 *      unvisited boundary vertex, follow a neighbour, repeat until we get
 *      back to the start. Each closed walk is one ring.
 *   5. Return the ring with the largest 2-D signed area — for a TIN with
 *      holes that's the outer perimeter; for disjoint polygons it's the
 *      biggest one.
 *
 * **Why XY-quantised vertex keys**: Two triangles that share a vertex in 3D
 * can have that vertex stored with sub-ULP differences (different float
 * round-trips through encode/decode). String-key on raw floats then misses
 * the match and edges that should fold into one stay distinct → every edge
 * counts as boundary → garbage outline. Quantising to 1 mm groups them
 * reliably without sacrificing real precision for stockpile work.
 *
 * **Use cases**:
 *   - **TIN** (many triangle polygons sharing edges): exact concave outline
 *     along the TIN's true perimeter.
 *   - **Single polygon**: returns the polygon itself (every edge appears once,
 *     they form one ring, which is just the polygon).
 *   - **Disjoint multi-polygons**: returns the largest individual polygon.
 *
 * Returns `null` only when the input has no valid edges.
 */
export function extractOuterBoundary(
  polygons: Array<Array<[number, number, number]>>,
): Array<[number, number, number]> | null {
  if (polygons.length === 0) return null;

  // 1 mm quantisation. Stockpile work doesn't need finer; tighter quantisation
  // increases the chance of TIN edges *not* matching due to floating-point
  // jitter through serialisation/deserialisation.
  const QUANT = 1000;
  const vkey = (v: [number, number, number]) =>
    `${Math.round(v[0] * QUANT)},${Math.round(v[1] * QUANT)}`;
  const ekey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

  const edgeCount = new Map<string, number>();
  const vertexByKey = new Map<string, [number, number, number]>();

  for (const poly of polygons) {
    if (poly.length < 3) continue;
    // Strip closing duplicate vertex if present so we don't fake an extra edge.
    const ring =
      poly.length > 1 &&
      poly[0][0] === poly[poly.length - 1][0] &&
      poly[0][1] === poly[poly.length - 1][1]
        ? poly.slice(0, -1)
        : poly;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const ka = vkey(a);
      const kb = vkey(b);
      if (ka === kb) continue; // degenerate edge
      vertexByKey.set(ka, a);
      vertexByKey.set(kb, b);
      const ek = ekey(ka, kb);
      edgeCount.set(ek, (edgeCount.get(ek) ?? 0) + 1);
    }
  }

  // Boundary adjacency map. Each boundary edge contributes to both endpoints.
  const adj = new Map<string, Set<string>>();
  for (const [ek, count] of edgeCount) {
    if (count !== 1) continue;
    const sep = ek.indexOf('|');
    const a = ek.slice(0, sep);
    const b = ek.slice(sep + 1);
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  }

  if (adj.size === 0) return null;

  // Walk closed rings from the boundary adjacency map.
  const visited = new Set<string>();
  const rings: Array<Array<string>> = [];
  for (const startKey of adj.keys()) {
    if (visited.has(startKey)) continue;
    const ring: string[] = [startKey];
    visited.add(startKey);
    let curr = startKey;
    let prev: string | null = null;
    let closed = false;
    while (true) {
      const nbs = adj.get(curr);
      if (!nbs) break;
      let next: string | null = null;
      for (const nb of nbs) {
        if (nb === prev) continue;
        if (nb === startKey) {
          // We've come back round — ring closed.
          closed = true;
          break;
        }
        if (!visited.has(nb)) {
          next = nb;
          break;
        }
      }
      if (closed || next === null) break;
      ring.push(next);
      visited.add(next);
      prev = curr;
      curr = next;
    }
    // Skip open chains — they indicate a malformed input (e.g. a polygon with
    // a missing edge). Not catastrophic, just unusable as an outer boundary.
    if (closed && ring.length >= 3) rings.push(ring);
  }

  if (rings.length === 0) return null;

  // Pick the ring with the largest 2-D area. Shoelace formula on XY.
  const ringArea = (ring: string[]) => {
    let twiceArea = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = vertexByKey.get(ring[i])!;
      const b = vertexByKey.get(ring[(i + 1) % ring.length])!;
      twiceArea += a[0] * b[1] - b[0] * a[1];
    }
    return Math.abs(twiceArea) * 0.5;
  };

  rings.sort((a, b) => ringArea(b) - ringArea(a));
  return rings[0].map((k) => vertexByKey.get(k)!);
}

/**
 * 2-D convex hull of a set of points (Andrew's monotone-chain algorithm).
 * O(n log n). Kept around as a last-resort fallback when
 * `extractOuterBoundary` returns `null` (e.g. for inputs whose edges don't
 * form any closed ring at all).
 *
 * Z values are preserved per output vertex (each output point is one of the
 * inputs verbatim — no interpolation).
 */
export function convexHullXY(
  points: Array<[number, number, number]>,
): Array<[number, number, number]> {
  if (points.length < 3) return [...points];
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (
    o: [number, number, number],
    a: [number, number, number],
    b: [number, number, number],
  ) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number, number]> = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Array<[number, number, number]> = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}
